/**
 * 검토 내용 저장·복원.
 *
 *  1) 브라우저 자동 저장 — 아무 조작 없이 유지된다. 단 그 PC·그 브라우저에서만.
 *  2) 파일 내보내기/불러오기 — 동료와 공유하거나 백업할 때.
 *
 * 저장·불러오기 모두 **사용자 PC 안에서만** 일어난다. 어떤 값도 외부로
 * 전송하지 않는다(CLAUDE.md 의 보안 요건).
 *
 * 불러오기는 남이 만든 파일이나 예전 판 파일이 들어올 수 있으므로
 * **형태를 검증하고, 고칠 수 있는 것은 고치되 무엇을 고쳤는지 알린다.**
 * 조용히 기본값으로 덮으면 설계자가 잘못된 값으로 계산서를 쓰게 된다.
 */
import { API_521_SCENARIOS } from "../data/scenarios";
import { FITTING_KINDS } from "../calc/fittings";
import { PIPE_ROUGHNESS_M } from "../calc/pipeFlow";
import {
  nextId,
  newScenarioRow,
  type CaseState,
  type ScenarioRow,
  type SimpleFluidKind,
} from "./caseState";
import type { ContingencyKind, InstallationKind } from "../calc/governingCase";
import {
  kindsForItem,
  newRateCalc,
  type RateCalcKind,
  type RateCalcState,
  type WettedGeometry,
} from "./rateCalc";
import {
  newPipingState,
  type PipeRunState,
  type PipingState,
} from "./pipingState";
import {
  newFlareScenario,
  newFlareSegment,
  newFlareState,
  newFlareValve,
  type FlarePipeSpec,
  type FlareScenarioState,
  type FlareSegmentState,
  type FlareState,
  type FlareValveState,
} from "./flareState";
import {
  newFlowsheetNode,
  type FlowsheetNodeForm,
  type FlowsheetState,
  type GasComponentEntryForm,
  type NodeKind,
  type SplitterBranchForm,
} from "./flowsheetState";
import { isPressureUnit, type PressureUnit } from "./pressureUnit";

/** 저장 형식 식별자. 다른 JSON 을 잘못 불러오는 것을 막는다. */
export const FILE_FORMAT = "psv-calculator-cases";
/** 저장 형식 판. 구조가 바뀌면 올리고 마이그레이션을 넣는다. */
export const SCHEMA_VERSION = 1;

const STORAGE_KEY = "psv-calculator/cases/v1";
const UNIT_KEY = "psv-calculator/pressureUnit/v1";
const PIPING_KEY = "psv-calculator/piping/v1";
const FLARE_KEY = "psv-calculator/flare/v1";
const FLOWSHEET_KEY = "psv-calculator/flowsheet/v1";

export interface SavedFile {
  readonly format: string;
  readonly version: number;
  readonly savedAt: string;
  readonly cases: readonly CaseState[];
  /** 배관 검토 — 나중에 붙은 기능이라 선택 항목이다. */
  readonly piping?: PipingState;
}

export type ImportResult =
  | {
      readonly ok: true;
      readonly cases: CaseState[];
      /** 파일에 배관 검토가 없으면 null — 화면은 기본값을 유지한다. */
      readonly piping: PipingState | null;
      readonly warnings: string[];
    }
  | { readonly ok: false; readonly message: string };

const VALID_ITEMS = new Set(API_521_SCENARIOS.map((s) => s.item));
const VALID_FLUIDS: ReadonlySet<string> = new Set([
  "gas",
  "steam",
  "liquid",
]);
const VALID_CONTINGENCY: ReadonlySet<string> = new Set(["operating", "fire"]);
const VALID_INSTALLATION: ReadonlySet<string> = new Set(["single", "multiple"]);
const VALID_GEOMETRY: ReadonlySet<string> = new Set([
  "manual",
  "vertical",
  "horizontal",
  "sphere",
]);
const VALID_PIPE_MATERIAL: ReadonlySet<string> = new Set(
  Object.keys(PIPE_ROUGHNESS_M),
);
const VALID_FITTING_KINDS: ReadonlySet<string> = new Set(FITTING_KINDS);
const VALID_BP_VALVE: ReadonlySet<string> = new Set([
  "conventional",
  "balanced-bellows",
  "pilot-operated",
]);
const VALID_LOSS_METHOD: ReadonlySet<string> = new Set([
  "equivalent-length",
  "resistance-coefficient",
]);
const VALID_FLOW_BASIS: ReadonlySet<string> = new Set(["rated", "required"]);
const VALID_EXEMPTION: ReadonlySet<string> = new Set([
  "none",
  "thermal-relief",
  "remote-sensed-pilot",
]);

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

/** 숫자 입력은 화면에서 문자열로 다루므로, 숫자로 들어와도 문자열로 되돌린다. */
function numStr(v: unknown, fallback: string): string {
  if (typeof v === "string") return v;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return fallback;
}

/**
 * 소요 분출용량 계산기의 입력을 복원한다.
 *
 * 이 블록은 계산기가 없던 판에서 저장한 파일에는 아예 없다. 그때는
 * 조용히 기본값을 쓴다 — 없는 게 정상이므로 경고하지 않는다.
 */
function normalizeRateCalc(raw: unknown, item: number): RateCalcState {
  const d = newRateCalc(item);
  if (typeof raw !== "object" || raw === null) return d;
  const o = raw as Record<string, unknown>;

  const kinds = kindsForItem(item);
  const kind =
    typeof o.kind === "string" && kinds.includes(o.kind as RateCalcKind)
      ? (o.kind as RateCalcKind)
      : d.kind;

  const geometry = VALID_GEOMETRY.has(String(o.geometry))
    ? (o.geometry as WettedGeometry)
    : d.geometry;

  const bool = (v: unknown, fallback: boolean) =>
    typeof v === "boolean" ? v : fallback;

  return {
    open: false, // 불러오면 계산기는 접힌 채로 시작한다.
    kind,
    appliedFlow: numStr(o.appliedFlow, d.appliedFlow),
    geometry,
    diameter: numStr(o.diameter, d.diameter),
    length: numStr(o.length, d.length),
    liquidHeight: numStr(o.liquidHeight, d.liquidHeight),
    wettedArea: numStr(o.wettedArea, d.wettedArea),
    environmentFactor: numStr(o.environmentFactor, d.environmentFactor),
    insulated: bool(o.insulated, d.insulated),
    drainage: bool(o.drainage, d.drainage),
    latentHeat: numStr(o.latentHeat, d.latentHeat),
    exposedArea: numStr(o.exposedArea, d.exposedArea),
    normalPressure: numStr(o.normalPressure, d.normalPressure),
    normalTemperature: numStr(o.normalTemperature, d.normalTemperature),
    wallTemperature: numStr(o.wallTemperature, d.wallTemperature),
    useDefaultFPrime: bool(o.useDefaultFPrime, d.useDefaultFPrime),
    alphaV: numStr(o.alphaV, d.alphaV),
    heatRate: numStr(o.heatRate, d.heatRate),
    relativeDensity: numStr(o.relativeDensity, d.relativeDensity),
    specificHeat: numStr(o.specificHeat, d.specificHeat),
    pdRatedFlow: numStr(o.pdRatedFlow, d.pdRatedFlow),
    pdDensity: numStr(o.pdDensity, d.pdDensity),
    pdSuctionPressure: numStr(o.pdSuctionPressure, d.pdSuctionPressure),
    pdSuctionTemperature: numStr(o.pdSuctionTemperature, d.pdSuctionTemperature),
    pdMolecularWeight: numStr(o.pdMolecularWeight, d.pdMolecularWeight),
    pdCompressibility: numStr(o.pdCompressibility, d.pdCompressibility),
    pdCapacityFactor: numStr(o.pdCapacityFactor, d.pdCapacityFactor),
    pipeInnerDiameter: numStr(o.pipeInnerDiameter, d.pipeInnerDiameter),
    pipeVelocity: numStr(o.pipeVelocity, d.pipeVelocity),
    pipeDensity: numStr(o.pipeDensity, d.pipeDensity),
    phInnerDiameter: numStr(o.phInnerDiameter, d.phInnerDiameter),
    phStraightLength: numStr(o.phStraightLength, d.phStraightLength),
    phFittingLength: numStr(o.phFittingLength, d.phFittingLength),
    phRoughness: numStr(o.phRoughness, d.phRoughness),
    phUpstreamPressure: numStr(o.phUpstreamPressure, d.phUpstreamPressure),
    phDownstreamPressure: numStr(o.phDownstreamPressure, d.phDownstreamPressure),
    phDensity: numStr(o.phDensity, d.phDensity),
    phViscosity: numStr(o.phViscosity, d.phViscosity),
    cvValue: numStr(o.cvValue, d.cvValue),
    cvUpstreamPressure: numStr(o.cvUpstreamPressure, d.cvUpstreamPressure),
    cvDownstreamPressure: numStr(o.cvDownstreamPressure, d.cvDownstreamPressure),
    cvDensity: numStr(o.cvDensity, d.cvDensity),
    cvFL: numStr(o.cvFL, d.cvFL),
    cvVaporPressure: numStr(o.cvVaporPressure, d.cvVaporPressure),
    cvCriticalPressure: numStr(o.cvCriticalPressure, d.cvCriticalPressure),
    cvTemperature: numStr(o.cvTemperature, d.cvTemperature),
    cvMolecularWeight: numStr(o.cvMolecularWeight, d.cvMolecularWeight),
    cvSpecificHeatRatio: numStr(o.cvSpecificHeatRatio, d.cvSpecificHeatRatio),
    cvCompressibility: numStr(o.cvCompressibility, d.cvCompressibility),
    cvXt: numStr(o.cvXt, d.cvXt),
    valveInnerDiameter: numStr(o.valveInnerDiameter, d.valveInnerDiameter),
    valveUpstreamPressure: numStr(o.valveUpstreamPressure, d.valveUpstreamPressure),
    valveDownstreamPressure: numStr(
      o.valveDownstreamPressure,
      d.valveDownstreamPressure,
    ),
    valveDensity: numStr(o.valveDensity, d.valveDensity),
    valveCd: numStr(o.valveCd, d.valveCd),
    valveTemperature: numStr(o.valveTemperature, d.valveTemperature),
    valveMolecularWeight: numStr(o.valveMolecularWeight, d.valveMolecularWeight),
    valveSpecificHeatRatio: numStr(
      o.valveSpecificHeatRatio,
      d.valveSpecificHeatRatio,
    ),
    valveCompressibility: numStr(o.valveCompressibility, d.valveCompressibility),
    tubeId: numStr(o.tubeId, d.tubeId),
    highSidePressure: numStr(o.highSidePressure, d.highSidePressure),
    tubeDensity: numStr(o.tubeDensity, d.tubeDensity),
    tubeCd: numStr(o.tubeCd, d.tubeCd),
    tubeTemperature: numStr(o.tubeTemperature, d.tubeTemperature),
    tubeMolecularWeight: numStr(
      o.tubeMolecularWeight,
      d.tubeMolecularWeight,
    ),
    tubeSpecificHeatRatio: numStr(
      o.tubeSpecificHeatRatio,
      d.tubeSpecificHeatRatio,
    ),
    tubeCompressibility: numStr(
      o.tubeCompressibility,
      d.tubeCompressibility,
    ),
  };
}

/**
 * 배관 검토 상태를 복원한다.
 *
 * 예전 판이 저장한 파일에는 이 블록이 없다. 그때는 null 을 돌려주고
 * 화면이 기본값을 그대로 쓰게 한다 — 없는 게 정상이므로 경고하지 않는다.
 */
function normalizePipeRun(raw: unknown, fallback: PipeRunState): PipeRunState {
  if (typeof raw !== "object" || raw === null) return fallback;
  const o = raw as Record<string, unknown>;

  const material = VALID_PIPE_MATERIAL.has(String(o.material))
    ? (o.material as PipeRunState["material"])
    : fallback.material;

  // 부속 목록은 알 수 없는 종류가 섞이면 그것만 버린다.
  const fittings = Array.isArray(o.fittings)
    ? o.fittings
        .filter(
          (f): f is { kind: string; count: number } =>
            typeof f === "object" &&
            f !== null &&
            VALID_FITTING_KINDS.has(String((f as { kind?: unknown }).kind)),
        )
        .map((f) => ({
          kind: f.kind as PipeRunState["fittings"][number]["kind"],
          count: Number.isFinite(Number(f.count))
            ? Math.max(0, Math.round(Number(f.count)))
            : 1,
        }))
    : fallback.fittings;

  return {
    nps: str(o.nps, fallback.nps),
    schedule: str(o.schedule, fallback.schedule),
    useCustomId:
      typeof o.useCustomId === "boolean" ? o.useCustomId : fallback.useCustomId,
    customIdMm: numStr(o.customIdMm, fallback.customIdMm),
    material,
    straightLengthM: numStr(o.straightLengthM, fallback.straightLengthM),
    fittings,
  };
}

function normalizePiping(raw: unknown): PipingState | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const d = newPipingState();

  const pick = <T extends string>(
    v: unknown,
    valid: ReadonlySet<string>,
    fallback: T,
  ): T => (valid.has(String(v)) ? (v as T) : fallback);

  const r = (o.reaction ?? {}) as Record<string, unknown>;

  return {
    tag: str(o.tag, d.tag),
    setPressure: numStr(o.setPressure, d.setPressure),
    mawp: numStr(o.mawp, d.mawp),
    allowableAccumulationPercent: numStr(
      o.allowableAccumulationPercent,
      d.allowableAccumulationPercent,
    ),
    valveType: pick(o.valveType, VALID_BP_VALVE, d.valveType),
    method: pick(o.method, VALID_LOSS_METHOD, d.method),
    // 구버전 저장본에는 이 항목이 없다 — 기본값(등온 압축성 ON)으로 채운다.
    outletCompressible:
      typeof o.outletCompressible === "boolean"
        ? o.outletCompressible
        : d.outletCompressible,
    outletMolecularWeight: numStr(
      o.outletMolecularWeight,
      d.outletMolecularWeight,
    ),
    outletTemperatureK: numStr(o.outletTemperatureK, d.outletTemperatureK),
    outletCompressibility: numStr(
      o.outletCompressibility,
      d.outletCompressibility,
    ),
    flowBasis: pick(o.flowBasis, VALID_FLOW_BASIS, d.flowBasis),
    exemption: pick(o.exemption, VALID_EXEMPTION, d.exemption),
    flowKgH: numStr(o.flowKgH, d.flowKgH),
    densityKgM3: numStr(o.densityKgM3, d.densityKgM3),
    viscosityCp: numStr(o.viscosityCp, d.viscosityCp),
    ruptureDiskDrop: numStr(o.ruptureDiskDrop, d.ruptureDiskDrop),
    inlet: normalizePipeRun(o.inlet, d.inlet),
    outlet: normalizePipeRun(o.outlet, d.outlet),
    superimposed: numStr(o.superimposed, d.superimposed),
    blowdown: numStr(o.blowdown, d.blowdown),
    forceBalanceOverpressure: numStr(
      o.forceBalanceOverpressure,
      d.forceBalanceOverpressure,
    ),
    reaction: {
      enabled: typeof r.enabled === "boolean" ? r.enabled : d.reaction.enabled,
      specificHeatRatio: numStr(
        r.specificHeatRatio,
        d.reaction.specificHeatRatio,
      ),
      stagnationTemperatureK: numStr(
        r.stagnationTemperatureK,
        d.reaction.stagnationTemperatureK,
      ),
      molecularWeight: numStr(r.molecularWeight, d.reaction.molecularWeight),
      outletIdMm: numStr(r.outletIdMm, d.reaction.outletIdMm),
      outletStaticPressure: numStr(
        r.outletStaticPressure,
        d.reaction.outletStaticPressure,
      ),
      usedRelievingTemperature:
        typeof r.usedRelievingTemperature === "boolean"
          ? r.usedRelievingTemperature
          : d.reaction.usedRelievingTemperature,
    },
  };
}

function normalizeRow(
  raw: unknown,
  where: string,
  warnings: string[],
): ScenarioRow {
  const d = newScenarioRow();
  if (typeof raw !== "object" || raw === null) {
    warnings.push(`${where}: 시나리오 형태가 아니어서 기본값으로 넣었습니다.`);
    return d;
  }
  const o = raw as Record<string, unknown>;

  let item = typeof o.item === "number" ? o.item : Number(o.item);
  if (!VALID_ITEMS.has(item)) {
    warnings.push(
      `${where}: 알 수 없는 시나리오 번호 ${String(o.item)} → 1번으로 되돌렸습니다.`,
    );
    item = 1;
  }

  const contingency = VALID_CONTINGENCY.has(String(o.contingency))
    ? (o.contingency as ContingencyKind)
    : (() => {
        warnings.push(`${where}: 상황 구분이 올바르지 않아 '운전'으로 두었습니다.`);
        return "operating" as const;
      })();

  const fluidKind = VALID_FLUIDS.has(String(o.fluidKind))
    ? (o.fluidKind as SimpleFluidKind)
    : (() => {
        warnings.push(
          `${where}: 유체 상태 '${String(o.fluidKind)}' 는 케이스 검토 화면이 지원하지 않아 '기체·증기'로 두었습니다.`,
        );
        return "gas" as const;
      })();

  return {
    id: nextId("row"),
    item,
    description: str(o.description),
    contingency,
    included: typeof o.included === "boolean" ? o.included : true,
    fluidKind,
    flow: numStr(o.flow, d.flow),
    temperature: numStr(o.temperature, d.temperature),
    molecularWeight: numStr(o.molecularWeight, d.molecularWeight),
    specificHeatRatio: numStr(o.specificHeatRatio, d.specificHeatRatio),
    compressibility: numStr(o.compressibility, d.compressibility),
    specificGravity: numStr(o.specificGravity, d.specificGravity),
    backpressure: numStr(o.backpressure, d.backpressure),
    rateCalc: normalizeRateCalc(o.rateCalc, item),
  };
}

function normalizeCase(
  raw: unknown,
  index: number,
  warnings: string[],
): CaseState | null {
  if (typeof raw !== "object" || raw === null) {
    warnings.push(`${index + 1}번째 밸브가 올바른 형태가 아니어서 건너뛰었습니다.`);
    return null;
  }
  const o = raw as Record<string, unknown>;
  const tag = str(o.tag).trim() || `PSV-${101 + index}`;
  const where = tag;

  const installation = VALID_INSTALLATION.has(String(o.installation))
    ? (o.installation as InstallationKind)
    : (() => {
        warnings.push(`${where}: 설치 형태가 올바르지 않아 '단일 설치'로 두었습니다.`);
        return "single" as const;
      })();

  const rawRows = Array.isArray(o.rows) ? o.rows : [];
  if (!Array.isArray(o.rows)) {
    warnings.push(`${where}: 시나리오 목록이 없어 빈 상태로 불러왔습니다.`);
  }

  return {
    id: nextId("case"),
    tag,
    equipment: str(o.equipment),
    setPressure: numStr(o.setPressure, "517"),
    installation,
    rows: rawRows.map((r, i) =>
      normalizeRow(r, `${where} 시나리오 ${i + 1}`, warnings),
    ),
  };
}

/**
 * 저장 파일 문자열을 만든다.
 *
 * 배관 검토는 나중에 붙은 기능이라 **선택 항목**으로 넣는다. 예전 판이
 * 저장한 파일에는 이 항목이 없고, 그때는 기본값으로 시작한다.
 */
export function serializeCases(
  cases: readonly CaseState[],
  piping?: PipingState,
): string {
  const payload: SavedFile = {
    format: FILE_FORMAT,
    version: SCHEMA_VERSION,
    savedAt: new Date().toISOString(),
    cases,
    ...(piping ? { piping } : {}),
  };
  return JSON.stringify(payload, null, 2);
}

/** 저장 파일 문자열을 화면 상태로 되돌린다. */
export function parseCases(text: string): ImportResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, message: "JSON 형식이 아닙니다. 파일을 확인하십시오." };
  }
  if (typeof data !== "object" || data === null) {
    return { ok: false, message: "내용이 비어 있거나 형식이 올바르지 않습니다." };
  }

  const o = data as Record<string, unknown>;
  if (o.format !== FILE_FORMAT) {
    return {
      ok: false,
      message:
        "이 프로그램이 저장한 파일이 아닙니다. " +
        `(형식 표시가 '${String(o.format ?? "없음")}' 입니다)`,
    };
  }

  const warnings: string[] = [];
  const version = typeof o.version === "number" ? o.version : 0;
  if (version > SCHEMA_VERSION) {
    warnings.push(
      `이 파일은 더 새로운 판(v${version})으로 저장되었습니다. ` +
        "일부 항목이 누락될 수 있으니 결과를 확인하십시오.",
    );
  }

  if (!Array.isArray(o.cases)) {
    return { ok: false, message: "밸브 목록을 찾을 수 없습니다." };
  }
  if (o.cases.length === 0) {
    return { ok: false, message: "파일에 밸브가 하나도 없습니다." };
  }

  const cases = o.cases
    .map((c, i) => normalizeCase(c, i, warnings))
    .filter((c): c is CaseState => c !== null);

  if (cases.length === 0) {
    return { ok: false, message: "불러올 수 있는 밸브가 없습니다." };
  }

  return { ok: true, cases, piping: normalizePiping(o.piping), warnings };
}

/* ── 브라우저 자동 저장 ──────────────────────────────────────
 * localStorage 는 시크릿 창·사이트 데이터 차단 등에서 접근 자체가
 * 예외를 던진다. 저장이 안 되더라도 계산은 계속되어야 하므로
 * 모든 접근을 try/catch 로 감싼다.
 */

export function saveToBrowser(cases: readonly CaseState[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, serializeCases(cases));
    return true;
  } catch {
    return false;
  }
}

export function loadFromBrowser(): CaseState[] | null {
  try {
    const text = localStorage.getItem(STORAGE_KEY);
    if (!text) return null;
    const r = parseCases(text);
    return r.ok ? r.cases : null;
  } catch {
    return null;
  }
}

export function clearBrowserSave(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* 저장소를 못 쓰는 환경이면 지울 것도 없다 */
  }
}

/**
 * 압력 단위는 밸브 데이터와 별도로 저장한다.
 * 파일을 주고받을 때 단위가 따라다니면, 받는 쪽이 자기 단위 설정을
 * 모르는 사이에 덮어쓰게 된다. 단위는 **그 사람의 화면 설정**으로 둔다.
 */
/**
 * 플레어 헤더 상태를 복원한다.
 *
 * 밸브·구간 목록이 길고 중첩이 깊어 항목별로 다 검사하면 코드가 배보다
 * 배꼽이 커진다. 대신 **형태만 확인하고 기본값으로 메운다** — 어차피
 * 계산 계층이 잘못된 값을 거부하고 화면에 이유를 띄운다.
 */
/** 시험에서 직접 부를 수 있게 공개한다 — 이전 형식 마이그레이션이 걸려 있다. */
export function normalizeFlare(raw: unknown): FlareState | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const d = newFlareState();

  const pipe = (v: unknown, fallback: FlarePipeSpec): FlarePipeSpec => {
    if (typeof v !== "object" || v === null) return fallback;
    const p = v as Record<string, unknown>;
    return {
      nps: str(p.nps, fallback.nps),
      schedule: str(p.schedule, fallback.schedule),
      useCustomId:
        typeof p.useCustomId === "boolean" ? p.useCustomId : fallback.useCustomId,
      customIdMm: numStr(p.customIdMm, fallback.customIdMm),
      material: VALID_PIPE_MATERIAL.has(String(p.material))
        ? (p.material as FlarePipeSpec["material"])
        : fallback.material,
      straightLengthM: numStr(p.straightLengthM, fallback.straightLengthM),
      fittings: Array.isArray(p.fittings)
        ? p.fittings
            .filter(
              (f): f is { kind: string; count: number } =>
                typeof f === "object" &&
                f !== null &&
                VALID_FITTING_KINDS.has(String((f as { kind?: unknown }).kind)),
            )
            .map((f) => ({
              kind: f.kind as FlarePipeSpec["fittings"][number]["kind"],
              count: Number.isFinite(Number(f.count))
                ? Math.max(0, Math.round(Number(f.count)))
                : 1,
            }))
        : fallback.fittings,
      fittingAllowancePercent: numStr(
        p.fittingAllowancePercent,
        fallback.fittingAllowancePercent,
      ),
    };
  };

  /*
   * 구간·밸브·시나리오는 서로를 id 로 가리킨다. 저장된 id 를 그대로
   * 살려야 연결이 유지되므로, 여기서는 **id 를 새로 발급하지 않는다**
   * (밸브 목록은 새로 발급하지만 그쪽은 서로를 가리키지 않는다).
   */
  const rawSegments = Array.isArray(o.segments) ? o.segments : null;

  /*
   * 예전 판(한 줄짜리 헤더)에는 `downstreamId` 가 없고 구간이 배열 순서대로
   * 이어졌다. 그대로 읽으면 모든 구간이 '팁 직결'이 되어 "팁이 둘 이상"
   * 오류가 난다. 연결 정보가 하나도 없으면 **저장된 순서를 사슬로 본다.**
   */
  const legacyChain =
    rawSegments !== null &&
    rawSegments.length > 1 &&
    !rawSegments.some(
      (raw) =>
        typeof (raw as Record<string, unknown> | null)?.downstreamId === "string",
    );

  const segments: FlareSegmentState[] = rawSegments
    ? rawSegments.map((raw, i) => {
        const seg = (raw ?? {}) as Record<string, unknown>;
        const fallback = newFlareSegment(`구간 ${i + 1}`, null);
        const id = str(seg.id, fallback.id);
        return {
          id,
          label: str(seg.label, fallback.label),
          downstreamId:
            typeof seg.downstreamId === "string"
              ? seg.downstreamId
              : legacyChain && i < rawSegments.length - 1
                ? str(
                    (rawSegments[i + 1] as Record<string, unknown> | null)?.id,
                    "",
                  )
                : null,
          pipe: pipe(seg.pipe, fallback.pipe),
        };
      })
    : d.segments;
  if (segments.length === 0) return null;

  const segIds = new Set(segments.map((x) => x.id));
  // 없는 구간을 가리키는 연결은 끊어 팁 직결로 되돌린다 —
  // 그러면 '팁이 둘'이라는 알아보기 쉬운 오류로 화면에 뜬다.
  const cleanedSegments = segments.map((seg) => ({
    ...seg,
    downstreamId:
      seg.downstreamId !== null && segIds.has(seg.downstreamId)
        ? seg.downstreamId
        : null,
  }));

  const fallbackSegmentId = cleanedSegments[0]!.id;
  const valves: FlareValveState[] = Array.isArray(o.valves)
    ? o.valves.map((raw, i) => {
        const v = (raw ?? {}) as Record<string, unknown>;
        const fallback = newFlareValve(`PSV-${101 + i}`, fallbackSegmentId);
        // 예전 판은 구간 번호(nodeIndex)로 붙었다.
        const legacyNode = Number(v.nodeIndex);
        const segId =
          str(v.segmentId, "") ||
          (Number.isFinite(legacyNode)
            ? (segments[
                Math.min(Math.max(0, Math.round(legacyNode)), segments.length - 1)
              ]?.id ?? "")
            : "");
        return {
          id: str(v.id, fallback.id),
          tag: str(v.tag, fallback.tag),
          molecularWeight: numStr(v.molecularWeight, fallback.molecularWeight),
          temperatureK: numStr(v.temperatureK, fallback.temperatureK),
          compressibility: numStr(v.compressibility, fallback.compressibility),
          viscosityCp: numStr(v.viscosityCp, fallback.viscosityCp),
          // 사라진 구간에 붙어 있었다면 첫 구간으로 옮긴다.
          segmentId: segIds.has(segId) ? segId : fallbackSegmentId,
          tail: pipe(v.tail, fallback.tail),
        };
      })
    : d.valves;

  const valveIds = new Set(valves.map((x) => x.id));

  /*
   * 예전 판은 조합 개념이 없었다. 밸브마다 유량 하나(`flowKgH`)와 이름
   * 하나(`scenarioName`)만 있었으므로, 그것을 조합 한 개로 옮긴다.
   */
  const legacyScenario = (): FlareScenarioState[] => {
    if (!Array.isArray(o.valves)) return [];
    const flows: Record<string, string> = {};
    o.valves.forEach((raw, i) => {
      const v = (raw ?? {}) as Record<string, unknown>;
      const id = valves[i]?.id;
      if (id !== undefined && v.flowKgH !== undefined) {
        flows[id] = numStr(v.flowKgH, "0");
      }
    });
    if (Object.keys(flows).length === 0) return [];
    return [newFlareScenario(str(o.scenarioName, "불러온 조합"), flows)];
  };

  const scenarios: FlareScenarioState[] = Array.isArray(o.scenarios)
    ? o.scenarios.map((raw, i) => {
        const sc = (raw ?? {}) as Record<string, unknown>;
        const flows: Record<string, string> = {};
        if (typeof sc.flows === "object" && sc.flows !== null) {
          for (const [k, val] of Object.entries(
            sc.flows as Record<string, unknown>,
          )) {
            // 사라진 밸브의 유량은 버린다.
            if (valveIds.has(k)) flows[k] = numStr(val, "0");
          }
        }
        return {
          id: str(sc.id, `sc-restored-${i}`),
          name: str(sc.name, `시나리오 ${i + 1}`),
          flows,
        };
      })
    : legacyScenario();

  // 시나리오가 하나도 없으면 빈 조합 하나를 만들어 화면이 성립하게 한다.
  const finalScenarios =
    scenarios.length > 0 ? scenarios : [newFlareScenario("시나리오 1")];
  const activeRaw = str(o.activeScenarioId, "");
  const activeScenarioId = finalScenarios.some((x) => x.id === activeRaw)
    ? activeRaw
    : finalScenarios[0]!.id;

  return {
    tipPressure: numStr(o.tipPressure, d.tipPressure),
    machLimit: numStr(o.machLimit, d.machLimit),
    valves,
    segments: cleanedSegments,
    scenarios: finalScenarios,
    activeScenarioId,
  };
}

export function saveFlareToBrowser(flare: FlareState): boolean {
  try {
    localStorage.setItem(FLARE_KEY, JSON.stringify(flare));
    return true;
  } catch {
    return false;
  }
}

export function loadFlareFromBrowser(): FlareState | null {
  try {
    const text = localStorage.getItem(FLARE_KEY);
    if (!text) return null;
    return normalizeFlare(JSON.parse(text));
  } catch {
    return null;
  }
}

const VALID_NODE_KINDS = new Set<string>([
  "source", "sink", "mixer", "splitter", "pipe", "heater", "scrubber", "valve",
  "reductionFurnace", "esfOffgas",
]);

function normalizeSplitterBranch(raw: unknown, fallback: SplitterBranchForm): SplitterBranchForm {
  if (typeof raw !== "object" || raw === null) return fallback;
  const o = raw as Record<string, unknown>;
  return {
    id: str(o.id, fallback.id),
    label: str(o.label, fallback.label),
    isFraction: typeof o.isFraction === "boolean" ? o.isFraction : fallback.isFraction,
    value: numStr(o.value, fallback.value),
  };
}

function normalizeGasComponentEntry(
  raw: unknown,
  fallback: GasComponentEntryForm,
): GasComponentEntryForm {
  if (typeof raw !== "object" || raw === null) return fallback;
  const o = raw as Record<string, unknown>;
  return {
    id: str(o.id, fallback.id),
    key: str(o.key, fallback.key),
    molePercent: numStr(o.molePercent, fallback.molePercent),
  };
}

/**
 * 노드 하나를 복원한다. 종류를 알 수 없으면(예: 나중 판에서 추가된 종류를
 * 예전 판이 읽는 경우) 통째로 버린다 — 어중간한 노드를 만드는 것보다
 * 안전하다.
 */
function normalizeFlowsheetNode(raw: unknown): FlowsheetNodeForm | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.kind !== "string" || !VALID_NODE_KINDS.has(o.kind)) return null;
  const kind = o.kind as NodeKind;
  const d = newFlowsheetNode(kind, []);
  const bool = (v: unknown, fallback: boolean) =>
    typeof v === "boolean" ? v : fallback;

  return {
    id: str(o.id, d.id),
    kind,
    label: str(o.label, d.label),
    inputId: str(o.inputId, d.inputId),
    inputIds: Array.isArray(o.inputIds)
      ? o.inputIds.filter((x): x is string => typeof x === "string")
      : d.inputIds,

    phase: o.phase === "liquid" ? "liquid" : "gas",
    compositionMode: o.compositionMode === "components" ? "components" : "average",
    dryGasMassFlowKgH: numStr(o.dryGasMassFlowKgH, d.dryGasMassFlowKgH),
    dryGasMolecularWeight: numStr(o.dryGasMolecularWeight, d.dryGasMolecularWeight),
    dryGasSpecificHeatJKgK: numStr(o.dryGasSpecificHeatJKgK, d.dryGasSpecificHeatJKgK),
    viscosityCp: numStr(o.viscosityCp, d.viscosityCp),
    components: Array.isArray(o.components)
      ? o.components.map((c, i) =>
          normalizeGasComponentEntry(c, d.components[i] ?? d.components[0]!),
        )
      : d.components,
    compressibility: numStr(o.compressibility, d.compressibility),
    waterVaporMassFlowKgH: numStr(o.waterVaporMassFlowKgH, d.waterVaporMassFlowKgH),
    massFlowKgH: numStr(o.massFlowKgH, d.massFlowKgH),
    densityKgM3: numStr(o.densityKgM3, d.densityKgM3),
    specificHeatJKgK: numStr(o.specificHeatJKgK, d.specificHeatJKgK),
    temperatureK: numStr(o.temperatureK, d.temperatureK),
    pressureGauge: numStr(o.pressureGauge, d.pressureGauge),

    oreFe2O3MassFlowKgH: numStr(o.oreFe2O3MassFlowKgH, d.oreFe2O3MassFlowKgH),
    metallizationPercent: numStr(o.metallizationPercent, d.metallizationPercent),
    carbonMassFlowKgH: numStr(o.carbonMassFlowKgH, d.carbonMassFlowKgH),

    nps: str(o.nps, d.nps),
    schedule: str(o.schedule, d.schedule),
    useCustomId: bool(o.useCustomId, d.useCustomId),
    customIdMm: numStr(o.customIdMm, d.customIdMm),
    straightLengthM: numStr(o.straightLengthM, d.straightLengthM),
    fittingEquivalentLengthM: numStr(o.fittingEquivalentLengthM, d.fittingEquivalentLengthM),
    material:
      typeof o.material === "string" && o.material in PIPE_ROUGHNESS_M
        ? (o.material as FlowsheetNodeForm["material"])
        : d.material,
    fittingK: numStr(o.fittingK, d.fittingK),

    branches: Array.isArray(o.branches)
      ? o.branches.map((b, i) => normalizeSplitterBranch(b, d.branches[i] ?? d.branches[0]!))
      : d.branches,

    dutyKw: numStr(o.dutyKw, d.dutyKw),

    outletTemperatureK: numStr(o.outletTemperatureK, d.outletTemperatureK),
    outletPressureGauge: numStr(o.outletPressureGauge, d.outletPressureGauge),
    saturationPercent: numStr(o.saturationPercent, d.saturationPercent),
    makeupWaterMassFlowKgH: numStr(o.makeupWaterMassFlowKgH, d.makeupWaterMassFlowKgH),

    pressureDropKpa: numStr(o.pressureDropKpa, d.pressureDropKpa),
  };
}

export function normalizeFlowsheet(raw: unknown): FlowsheetState | null {
  if (typeof raw !== "object" || raw === null) return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.nodes)) return null;
  const nodes = o.nodes
    .map(normalizeFlowsheetNode)
    .filter((n): n is FlowsheetNodeForm => n !== null);
  if (nodes.length === 0) return null;
  return { nodes };
}

export function saveFlowsheetToBrowser(flowsheet: FlowsheetState): boolean {
  try {
    localStorage.setItem(FLOWSHEET_KEY, JSON.stringify(flowsheet));
    return true;
  } catch {
    return false;
  }
}

export function loadFlowsheetFromBrowser(): FlowsheetState | null {
  try {
    const text = localStorage.getItem(FLOWSHEET_KEY);
    if (!text) return null;
    return normalizeFlowsheet(JSON.parse(text));
  } catch {
    return null;
  }
}

export function savePipingToBrowser(piping: PipingState): boolean {
  try {
    localStorage.setItem(PIPING_KEY, JSON.stringify(piping));
    return true;
  } catch {
    return false;
  }
}

export function loadPipingFromBrowser(): PipingState | null {
  try {
    const text = localStorage.getItem(PIPING_KEY);
    if (!text) return null;
    return normalizePiping(JSON.parse(text));
  } catch {
    return null;
  }
}

export function saveUnitToBrowser(unit: PressureUnit): void {
  try {
    localStorage.setItem(UNIT_KEY, unit);
  } catch {
    /* 저장 못 해도 계산은 계속된다 */
  }
}

export function loadUnitFromBrowser(): PressureUnit | null {
  try {
    const v = localStorage.getItem(UNIT_KEY);
    return isPressureUnit(v) ? v : null;
  } catch {
    return null;
  }
}

/* ── 파일 내보내기 / 불러오기 ───────────────────────────────── */

/** 오늘 날짜가 들어간 파일명. 예: PSV-검토-2026-08-28.json */
export function defaultFileName(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `PSV-검토-${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}.json`;
}

/** 브라우저가 파일을 내려받게 한다. 서버를 거치지 않는다. */
export function downloadJson(text: string, fileName: string): void {
  const blob = new Blob([text], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
